import { useEffect, useRef, useState } from 'react';
import { animate } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Activity } from 'lucide-react';

interface DataPoint {
    label: string;
    value: number;
}

interface ActivityChartProps {
    data: DataPoint[];
    title?: string;
    color?: 'ember' | 'safety' | 'cyan';
}

const colorMap = {
    ember: {
        primary: '#ff6b35',
        gradient: ['#ff3b00', '#ff6b35'],
        bg: 'rgba(255, 107, 53, 0.1)',
    },
    safety: {
        primary: '#10b981',
        gradient: ['#059669', '#10b981'],
        bg: 'rgba(16, 185, 129, 0.1)',
    },
    cyan: {
        primary: '#00d4ff',
        gradient: ['#00a8ff', '#00d4ff'],
        bg: 'rgba(0, 212, 255, 0.1)',
    },
};

/**
 * ActivityChart - Animated bar chart showing recent activity trends
 * Features staggered bar animations and hover effects
 */
export default function ActivityChart({
    data,
    title = 'Recent Activity',
    color = 'ember',
}: ActivityChartProps) {
    const { ref, isInView } = useScrollAnimation<HTMLDivElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const barsRef = useRef<(HTMLDivElement | null)[]>([]);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const maxValue = Math.max(...data.map(d => d.value), 1);
    const colors = colorMap[color];

    useEffect(() => {
        if (!isInView || reducedMotion) return;

        barsRef.current.forEach((bar, index) => {
            if (!bar) return;
            const targetHeight = (data[index].value / maxValue) * 100;

            animate(bar, {
                height: [`0%`, `${targetHeight}%`],
                opacity: [0, 1],
                duration: 800,
                delay: index * 80,
                ease: 'outExpo',
            });
        });
    }, [isInView, data, maxValue, reducedMotion]);

    return (
        <div
            ref={ref}
            className={`card-terminal p-6 ${isInView ? 'animate-fadeIn' : 'opacity-0'}`}
        >
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: colors.bg }}
                >
                    <Activity className="w-5 h-5" style={{ color: colors.primary }} />
                </div>
                <div>
                    <h3 className="font-display font-semibold text-white">{title}</h3>
                    <p className="text-sm text-gray-500">Last 7 days</p>
                </div>
            </div>

            {/* Chart */}
            <div className="relative h-48">
                {/* Y-axis lines */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {[100, 75, 50, 25, 0].map((val) => (
                        <div key={val} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-6">{val}%</span>
                            <div className="flex-1 border-b border-[var(--blueprint-line)]" />
                        </div>
                    ))}
                </div>

                {/* Bars */}
                <div className="absolute bottom-0 left-8 right-0 h-[calc(100%-1rem)] flex items-end justify-around gap-2">
                    {data.map((point, index) => {
                        const height = reducedMotion ? `${(point.value / maxValue) * 100}%` : '0%';
                        const isHovered = hoveredIndex === index;

                        return (
                            <div
                                key={index}
                                className="relative flex flex-col items-center flex-1 max-w-12"
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                {/* Tooltip */}
                                {isHovered && (
                                    <div className="absolute bottom-full mb-2 px-2 py-1 bg-charcoal-900 text-white text-xs rounded-lg whitespace-nowrap z-10">
                                        {point.value} audits
                                    </div>
                                )}

                                {/* Bar */}
                                <div
                                    ref={el => barsRef.current[index] = el}
                                    className={`w-full rounded-t-lg transition-all duration-200 cursor-pointer ${isHovered ? 'scale-105' : ''
                                        }`}
                                    style={{
                                        height,
                                        background: `linear-gradient(to top, ${colors.gradient[0]}, ${colors.gradient[1]})`,
                                        boxShadow: isHovered ? `0 4px 20px ${colors.primary}40` : 'none',
                                    }}
                                />

                                {/* Label */}
                                <span className="text-xs text-gray-400 mt-2">{point.label}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
